import { comparePriority } from './priority.js';
import type { InterventionSummary } from './schemas/dossiers.js';

/**
 * Règles « À traiter » et « À faire maintenant » (décision du porteur du 02/10/2026, Accueil « Ma journée »).
 * **Une seule définition** pour l'interface (puces d'« À l'atelier » et d'« Interventions », Accueil), les
 * maquettes et, au besoin, le serveur. Aucune donnée personnelle ni montant : seulement l'état du dossier.
 */

/** Champs d'un dossier lus par ces règles (sous-ensemble de `InterventionSummary`). */
export type AttentionInput = Pick<
  InterventionSummary,
  'status' | 'pending' | 'urgency' | 'storageState' | 'daysOnSite' | 'readyAt' | 'idle' | 'idleDays'
> & { pinned?: boolean };

// ─── À traiter ──────────────────────────────────────────────────────────────

/** Raison pour laquelle une action du garage est attendue sur un dossier (puce « À traiter »). */
export type TodoReason = 'question' | 'unread' | 'decision' | 'validate' | 'change' | 'start' | 'late';

export const TODO_REASON_LABELS: Record<TodoReason, string> = {
  question: 'Question du client sans réponse',
  unread: 'Message du client non lu',
  decision: 'Décision du client à appliquer',
  validate: 'Travaux complémentaires à valider',
  change: 'Modification demandée par le client',
  start: 'Réceptionné, travaux non démarrés',
  late: 'En retard',
};

/**
 * **À traiter** = une action du garage est attendue. Règles, dans l'ordre :
 * - `question` : message du client non traité (`pending.openQuestions`) ;
 * - `unread` : message du client non lu par ce membre (messagerie, `unreadCount`) ;
 * - `decision` : décision du client reçue **alors que le dossier est encore « Attente décision client »**
 *   (l'API garde une décision « reçue » jusqu'à la clôture, sans accusé de prise en compte : une
 *   décision déjà appliquée, dossier repassé en cours ou prêt, n'est plus « à traiter ») ;
 * - `validate` : travaux complémentaires à valider (`pending.complementsToValidate`) ;
 * - `change` : modification demandée par le client (`pending.changeRequests`) ;
 * - `start` : véhicule réceptionné, travaux non démarrés (statut `received`) ;
 * - `late` : dossier en retard (`urgency = late`, calculé par le serveur).
 * Un véhicule **prêt** attend seulement le client : il n'est « à traiter » que pour un message du client
 * (question ou non-lu).
 */
export function todoReasons(i: Pick<AttentionInput, 'status' | 'pending' | 'urgency'>, unread = 0): TodoReason[] {
  if (i.status === 'closed') return [];
  const p = i.pending;
  const reasons: TodoReason[] = [];
  if (p.openQuestions > 0) reasons.push('question');
  if (unread > 0) reasons.push('unread');
  if (i.status === 'ready') return reasons;
  if (p.decisionsReceived > 0 && i.status === 'awaiting_decision') reasons.push('decision');
  if (p.complementsToValidate > 0) reasons.push('validate');
  if ((p.changeRequests ?? 0) > 0) reasons.push('change');
  if (i.status === 'received') reasons.push('start');
  if (i.urgency === 'late') reasons.push('late');
  return reasons;
}

// ─── À faire maintenant (Accueil) ───────────────────────────────────────────

/**
 * Raison d'une ligne de « À faire maintenant » : celles d'« À traiter », plus le **gardiennage dépassé**
 * (véhicule prêt dont le délai gratuit est passé : relancer le client) et le dossier **sans action** depuis
 * le seuil du garage (`idle` du serveur ; jamais pour un véhicule prêt, qui attend le client). 03/10/2026 :
 * dossier **épinglé** par le membre (`pinned`), même sans autre raison (dernière raison affichée).
 */
export type AttentionReason = TodoReason | 'storage' | 'idle' | 'pinned';

/** Ordre d'importance des raisons d'un même dossier (la première est affichée). */
export const ATTENTION_REASON_ORDER: readonly AttentionReason[] = [
  'question',
  'unread',
  'decision',
  'change',
  'validate',
  'storage',
  'start',
  'late',
  'idle',
  'pinned',
];

/** Ton de la bordure de la ligne (jetons sémantiques de l'interface, jamais la couleur du garage). */
export type AttentionTone = 'warning' | 'info' | 'support' | 'danger' | 'received' | 'neutral';
export const ATTENTION_TONES: Record<AttentionReason, AttentionTone> = {
  question: 'warning',
  unread: 'warning',
  change: 'warning',
  decision: 'info',
  validate: 'support',
  storage: 'danger',
  late: 'danger',
  start: 'received',
  idle: 'neutral',
  pinned: 'info',
};

/** Raisons d'« À faire maintenant », dans l'ordre d'importance. Vide : rien à faire sur ce dossier. */
export function attentionReasons(i: AttentionInput, unread = 0): AttentionReason[] {
  if (i.status === 'closed') return [];
  const set = new Set<AttentionReason>(todoReasons(i, unread));
  if (i.status === 'ready' && i.storageState === 'overdue') set.add('storage');
  if (i.status !== 'ready' && i.idle) set.add('idle');
  if (i.pinned) set.add('pinned');
  return ATTENTION_REASON_ORDER.filter((r) => set.has(r));
}

const DAY = 86_400_000;

/** Jours entiers écoulés depuis `iso` (0 le jour même ; jamais négatif). */
function daysSince(iso: string, now: Date): number {
  const t = Date.parse(iso);
  return Number.isFinite(t) ? Math.max(0, Math.floor((now.getTime() - t) / DAY)) : 0;
}

/**
 * Raison **en clair**, en minuscules (l'interface met la majuscule) : « décision du client reçue : à
 * appliquer », « prête depuis 8 j, gardiennage dépassé », « en retard, 13 j », « sans action depuis 4 j »…
 */
export function attentionLabel(reason: AttentionReason, i: AttentionInput, now: Date = new Date()): string {
  switch (reason) {
    case 'question':
      return 'question du client sans réponse';
    case 'unread':
      return 'message du client non lu';
    case 'decision':
      return 'décision du client reçue : à appliquer';
    case 'change':
      return 'modification demandée par le client';
    case 'validate':
      return 'travaux complémentaires à valider';
    case 'storage':
      return i.readyAt ? `prête depuis ${daysSince(i.readyAt, now)} j, gardiennage dépassé` : 'prête, gardiennage dépassé';
    case 'start':
      return 'réceptionnée, pas démarrée';
    case 'late':
      return `en retard, ${i.daysOnSite} j`;
    case 'idle':
      return `sans action depuis ${i.idleDays} j`;
    case 'pinned':
      return 'épinglé';
  }
}

export interface AttentionItem<T> {
  item: T;
  reasons: AttentionReason[];
}

/**
 * « À faire maintenant » : les dossiers qui ont au moins une raison, triés par urgence (`comparePriority`,
 * la règle du tri « Priorité » de l'API et d'« À l'atelier »).
 */
export function attentionList<T extends AttentionInput & { id: string; receivedAt: string }>(
  items: readonly T[],
  unreadOf: (id: string) => number = () => 0,
): AttentionItem<T>[] {
  return items
    .map((item) => ({ item, reasons: attentionReasons(item, unreadOf(item.id)) }))
    .filter((x) => x.reasons.length > 0)
    .sort((a, b) => comparePriority(a.item, b.item));
}

// ─── Pastille de l'application installée (03/10/2026) ───────────────────────

/** Plafond du chiffre de la pastille (icône de l'application) : comme le menu, « 99 » au plus. */
export const APP_BADGE_MAX = 99;

/**
 * Décisions de clients **à appliquer** (règle `decision` d'« À traiter ») : décisions reçues sur des
 * dossiers encore « Attente décision client ». Une décision déjà appliquée ne compte plus.
 */
export function decisionsToApply(items: readonly Pick<AttentionInput, 'status' | 'pending'>[]): number {
  return items.reduce((n, i) => n + (i.status === 'awaiting_decision' ? Math.max(0, i.pending.decisionsReceived) : 0), 0);
}

/**
 * Chiffre de la pastille sur l'icône de l'application installée, pour un membre : conversations de
 * clients non lues + décisions à appliquer. Entier borné à `[0, APP_BADGE_MAX]` (0 : pastille effacée).
 */
export function appBadgeCount(unread: number, decisions: number): number {
  const n = Math.trunc((Number.isFinite(unread) ? Math.max(0, unread) : 0) + (Number.isFinite(decisions) ? Math.max(0, decisions) : 0));
  return Math.min(APP_BADGE_MAX, n);
}
